import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { llmService } from '../services/llmService';

// =====================================
// ASYNC THUNKS
// =====================================

/**
 * Query the LLM with optional context
 */
export const queryLLM = createAsyncThunk(
  'dataAgent/queryLLM',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await llmService.queryLLM(payload);
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error?.message || 'Failed to query LLM');
    }
  }
);

/**
 * Analyze bulk EOB documents
 */
export const analyzeBulkEob = createAsyncThunk(
  'dataAgent/analyzeBulkEob',
  async ({ file, bulkContext }, { rejectWithValue }) => {
    try {
      const response = await llmService.analyzeBulkEob(file, bulkContext);
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error?.message || 'Failed to analyze EOB');
    }
  }
);

/**
 * Extract claim data from email body with optional attachments
 */
export const extractClaimFromEmail = createAsyncThunk(
  'dataAgent/extractClaimFromEmail',
  async ({ emailBody, messageId, attachments }, { rejectWithValue }) => {
    try {
      const response = await llmService.extractFromEmail(emailBody, messageId, attachments);
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error?.message || 'Failed to extract from email');
    }
  }
);

/**
 * Extract claim data from image
 */
export const extractClaimFromImage = createAsyncThunk(
  'dataAgent/extractClaimFromImage',
  async (payload, { rejectWithValue }) => {
    try {
      let file, templateId;
      if (payload && payload.file) {
        file = payload.file;
        templateId = payload.templateId;
      } else {
        file = payload;
      }
      const response = await llmService.extractClaimFromImage(file, templateId);
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error?.message || 'Failed to extract from image');
    }
  }
);

// =====================================
// INITIAL STATE
// =====================================
const initialState = {
  // Conversation state (for side panel)
  conversation: [],

  // Side panel state
  activePageContext: null, // Context set by the active page/component
  sidePanel: {
    open: false,
    context: null, // Explicit context passed when opening panel
  },

  // Analysis results (for EOB/Email extraction)
  bulkEobResult: null,
  claimExtraction: null,

  // Loading states
  isLoadingResponse: false,
  isLoadingAnalysis: false,

  // Error state
  error: null,
};

// =====================================
// SLICE
// =====================================
const dataAgentSlice = createSlice({
  name: 'dataAgent',
  initialState,
  reducers: {
    // Conversation management
    setConversation: (state, action) => {
      state.conversation = action.payload;
    },
    addMessageToConversation: (state, action) => {
      state.conversation.push(action.payload);
    },
    updateMessageInConversation: (state, action) => {
      const { messageId, updates } = action.payload;
      const messageIndex = state.conversation.findIndex(m => m.id === messageId);
      if (messageIndex !== -1) {
        state.conversation[messageIndex] = { ...state.conversation[messageIndex], ...updates };
      }
    },

    // Side panel management
    openDataAgentPanel: (state, action) => {
      state.sidePanel.open = true;
      state.sidePanel.context = action.payload || null;
    },
    closeDataAgentPanel: (state) => {
      state.sidePanel.open = false;
      state.sidePanel.context = null;
    },

    // Context management (set by pages/components)
    setActivePageContext: (state, action) => {
      state.activePageContext = action.payload;
    },

    // Error management
    clearError: (state) => {
      state.error = null;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },

    // Analysis results management
    clearBulkEobResult: (state) => {
      state.bulkEobResult = null;
    },

    // Reset state
    resetDataAgentState: () => initialState,
  },
  extraReducers: (builder) => {
    // queryLLM
    builder
      .addCase(queryLLM.pending, (state) => {
        state.isLoadingResponse = true;
        state.error = null;
      })
      .addCase(queryLLM.fulfilled, (state) => {
        state.isLoadingResponse = false;
      })
      .addCase(queryLLM.rejected, (state, action) => {
        state.isLoadingResponse = false;
        state.error = action.payload;
      });

    // analyzeBulkEob
    builder
      .addCase(analyzeBulkEob.pending, (state) => {
        state.isLoadingAnalysis = true;
        state.error = null;
      })
      .addCase(analyzeBulkEob.fulfilled, (state, action) => {
        state.isLoadingAnalysis = false;
        state.bulkEobResult = action.payload;
      })
      .addCase(analyzeBulkEob.rejected, (state, action) => {
        state.isLoadingAnalysis = false;
        state.error = action.payload;
      });

    // extractClaimFromEmail
    builder
      .addCase(extractClaimFromEmail.pending, (state) => {
        state.isLoadingAnalysis = true;
        state.error = null;
      })
      .addCase(extractClaimFromEmail.fulfilled, (state, action) => {
        state.isLoadingAnalysis = false;
        state.claimExtraction = action.payload;
      })
      .addCase(extractClaimFromEmail.rejected, (state, action) => {
        state.isLoadingAnalysis = false;
        state.error = action.payload;
      });

    // extractClaimFromImage
    builder
      .addCase(extractClaimFromImage.pending, (state) => {
        state.isLoadingAnalysis = true;
        state.error = null;
      })
      .addCase(extractClaimFromImage.fulfilled, (state, action) => {
        state.isLoadingAnalysis = false;
        state.claimExtraction = action.payload;
      })
      .addCase(extractClaimFromImage.rejected, (state, action) => {
        state.isLoadingAnalysis = false;
        state.error = action.payload;
      });
  },
});

// =====================================
// EXPORTS
// =====================================
export const {
  setConversation,
  addMessageToConversation,
  updateMessageInConversation,
  openDataAgentPanel,
  closeDataAgentPanel,
  setActivePageContext,
  clearError,
  setError,
  clearBulkEobResult,
  resetDataAgentState,
} = dataAgentSlice.actions;

export default dataAgentSlice.reducer;